import React, { useState, useMemo } from 'react';
import {
  ActionIcon,
  Badge,
  Group,
  Loader,
  Paper,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Modal,
  Code,
  Tooltip,
  Select,
  Flex,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconJson, IconCopy } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useSearchAuditLogs } from '../../../global/entities/SystemAuditLog/queries/useSearchAuditLogs';
import { useAuditLogMetadata } from '../../../global/entities/SystemAuditLog/queries/useAuditLogMetadata';
import { AuditLogFilters } from '../components/AuditLogFilters';
import { ISearchAuditLogsFilter, IAuditLogItem } from '../../../global/entities/SystemAuditLog/SystemAuditLog';
import { notifications } from '@mantine/notifications';

interface ExpandedRow {
  [key: number]: boolean;
}

interface DetailModalState {
  isOpen: boolean;
  item: IAuditLogItem | null;
}

const getActionColor = (action: string): string => {
  switch (action?.toLowerCase()) {
    case 'created':
      return 'green';
    case 'modified':
      return 'blue';
    case 'deactivated':
    case 'deleted':
      return 'red';
    case 'revoked':
      return 'orange';
    default:
      return 'gray';
  }
};

export const SystemAuditLogTable: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ISearchAuditLogsFilter>({
    pageNumber: 1,
    pageSize: 25,
    sortBy: 'EventDate',
    sortDirection: 'DESC',
  });
  const [expandedRows, setExpandedRows] = useState<ExpandedRow>({});
  const [detailModal, setDetailModal] = useState<DetailModalState>({ isOpen: false, item: null });

  const { data: metadata, isLoading: metadataLoading } = useAuditLogMetadata();
  const { data: searchResult, isLoading: searchLoading } = useSearchAuditLogs(filter);

  const handleSearch = () => {
    setFilter({ ...filter, pageNumber: 1 });
  };

  const handleClearFilters = () => {
    setFilter({
      pageNumber: 1,
      pageSize: 25,
      sortBy: 'EventDate',
      sortDirection: 'DESC',
    });
  };

  const handlePageSizeChange = (value: string | null) => {
    if (value) {
      setFilter({
        ...filter,
        pageSize: parseInt(value),
        pageNumber: 1,
      });
    }
  };

  const toggleRowExpanded = (id: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openDetailModal = (item: IAuditLogItem) => {
    setDetailModal({ isOpen: true, item });
  };

  const closeDetailModal = () => {
    setDetailModal({ isOpen: false, item: null });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notifications.show({
      title: t('notification.success'),
      message: t('auditLog.detailsCopied'),
      color: 'green',
      autoClose: 2000,
    });
  };

  const rows = useMemo(() => {
    if (!searchResult?.items) return [];

    return searchResult.items.map((item) => [
      <Table.Tr key={`main-${item.id}`}>
        <Table.Td width="40">
          <ActionIcon
            size="xs"
            variant="subtle"
            onClick={() => toggleRowExpanded(item.id)}
          >
            {expandedRows[item.id] ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td>{item.id}</Table.Td>
        <Table.Td>{new Date(item.eventDate).toLocaleString()}</Table.Td>
        <Table.Td>
          <Badge size="sm" variant="light">
            {item.category}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge size="sm" color={getActionColor(item.action)} variant="dot">
            {item.action}
          </Badge>
        </Table.Td>
        <Table.Td>{item.entityType || '-'}</Table.Td>
        <Table.Td>{item.entityId || '-'}</Table.Td>
        <Table.Td>{item.actorUserId || 'System'}</Table.Td>
        <Table.Td>
          <Tooltip label={item.ipAddress || '-'} multiline maw={200}>
            <Text size="sm" truncate>
              {item.ipAddress || '-'}
            </Text>
          </Tooltip>
        </Table.Td>
        <Table.Td>
          {item.details && (
            <ActionIcon
              size="sm"
              variant="light"
              onClick={() => openDetailModal(item)}
              title={t('auditLog.viewDetails')}
            >
              <IconJson size={16} />
            </ActionIcon>
          )}
        </Table.Td>
      </Table.Tr>,
      expandedRows[item.id] && (
        <Table.Tr key={`expanded-${item.id}`}>
          <Table.Td colSpan={10}>
            <Stack gap="sm" p="md" style={{ backgroundColor: '#f8f9fa' }}>
              {item.userAgent && (
                <div>
                  <Text size="sm" fw={500}>
                    {t('auditLog.userAgent')}
                  </Text>
                  <Text size="sm" c="dimmed" truncate>
                    {item.userAgent}
                  </Text>
                </div>
              )}
              {item.details && (
                <div>
                  <Text size="sm" fw={500}>
                    {t('auditLog.details')}
                  </Text>
                  <Code block size="xs" style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(item.details, null, 2)}
                  </Code>
                </div>
              )}
            </Stack>
          </Table.Td>
        </Table.Tr>
      ),
    ]);
  }, [searchResult?.items, expandedRows, t]);

  return (
    <Stack gap="lg">
      <Title order={2}>{t('navbar.auditLogs')}</Title>

      <AuditLogFilters
        filter={filter}
        metadata={metadata}
        isLoading={searchLoading || metadataLoading}
        onFilterChange={setFilter}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
      />

      <Paper p="md" radius="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {t('auditLog.totalRecords', { count: searchResult?.totalCount || 0 })}
            </Text>
            <Flex gap="sm" align="center">
              <Select
                label={t('auditLog.pageSize')}
                value={filter.pageSize?.toString()}
                onChange={handlePageSizeChange}
                data={['25', '50', '100']}
                w={100}
              />
            </Flex>
          </Group>

          <ScrollArea>
            {searchLoading ? (
              <Loader />
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th width="40"></Table.Th>
                    <Table.Th>{t('auditLog.id')}</Table.Th>
                    <Table.Th>{t('auditLog.eventDate')}</Table.Th>
                    <Table.Th>{t('auditLog.category')}</Table.Th>
                    <Table.Th>{t('auditLog.action')}</Table.Th>
                    <Table.Th>{t('auditLog.entityType')}</Table.Th>
                    <Table.Th>{t('auditLog.entityId')}</Table.Th>
                    <Table.Th>{t('auditLog.actor')}</Table.Th>
                    <Table.Th>{t('auditLog.ipAddress')}</Table.Th>
                    <Table.Th width="40"></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            )}
          </ScrollArea>

          {searchResult && searchResult.totalPages > 1 && (
            <Group justify="center">
              <Pagination
                value={filter.pageNumber}
                onChange={(page) => setFilter({ ...filter, pageNumber: page })}
                total={searchResult.totalPages}
              />
            </Group>
          )}
        </Stack>
      </Paper>

      <Modal
        opened={detailModal.isOpen}
        onClose={closeDetailModal}
        title={t('auditLog.detailsTitle')}
        size="lg"
      >
        {detailModal.item && (
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>{t('auditLog.details')}</Title>
              <ActionIcon
                variant="light"
                onClick={() =>
                  copyToClipboard(JSON.stringify(detailModal.item?.details, null, 2))
                }
                title={t('button.copy')}
              >
                <IconCopy size={16} />
              </ActionIcon>
            </Group>
            <Code block size="sm" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {JSON.stringify(detailModal.item.details, null, 2)}
            </Code>
            <Stack gap="xs">
              <Group>
                <Text fw={500} size="sm">
                  {t('auditLog.eventId')}:
                </Text>
                <Text size="sm">{detailModal.item.id}</Text>
              </Group>
              <Group>
                <Text fw={500} size="sm">
                  {t('auditLog.eventDate')}:
                </Text>
                <Text size="sm">{new Date(detailModal.item.eventDate).toLocaleString()}</Text>
              </Group>
              <Group>
                <Text fw={500} size="sm">
                  {t('auditLog.category')}:
                </Text>
                <Badge size="sm">{detailModal.item.category}</Badge>
              </Group>
              <Group>
                <Text fw={500} size="sm">
                  {t('auditLog.action')}:
                </Text>
                <Badge size="sm" color={getActionColor(detailModal.item.action)}>
                  {detailModal.item.action}
                </Badge>
              </Group>
              {detailModal.item.userAgent && (
                <Group>
                  <Text fw={500} size="sm">
                    {t('auditLog.userAgent')}:
                  </Text>
                  <Text size="sm" truncate>
                    {detailModal.item.userAgent}
                  </Text>
                </Group>
              )}
              {detailModal.item.clientCommitHash && (
                <Group>
                  <Text fw={500} size="sm">
                    {t('auditLog.clientCommitHash')}:
                  </Text>
                  <Text size="sm" ff="monospace">
                    {detailModal.item.clientCommitHash}
                  </Text>
                </Group>
              )}
              {detailModal.item.serverCommitHash && (
                <Group>
                  <Text fw={500} size="sm">
                    {t('auditLog.serverCommitHash')}:
                  </Text>
                  <Text size="sm" ff="monospace">
                    {detailModal.item.serverCommitHash}
                  </Text>
                </Group>
              )}
            </Stack>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};
